// DocumentReferenceCardExtended.jsx
import React, { useEffect, useState } from "react";
import { useStore } from "react-redux";
import { Modal } from "react-bootstrap";
import styles from "../modals/ResourceViewerModal.module.css";
import drstyles from "./DocumentReferenceCardExtended.module.css";
import FemalePatient from "../assets/Patient_Female_96.png";
import MalePatient from "../assets/Patient_Male_96.png";
import FemaleProvider from "../assets/Doctor_Female.jpg";
import MaleProvider from "../assets/Doctor_Male_96.png";
import DefaultOrg from "../assets/world-health.png";
import PDFViewer from "../utils/PDFViewer";
import { fetchPatientByReference } from "../services/patientService";
import { fetchProviderByReference } from "../services/providerService";
import { fetchOrganizationByReference } from "../services/organizationService";
import { fetchEncounterByReference } from "../services/encounterService";
import { Field, renderReference, SectionHeader, renderCodeable } from "../utils/renderUtils";
import { resolveName } from "../utils/messageUtils";
import AttachmentHandler from "../components/AttachmentHandler";
import CardHeader from "./CardHeader";

/* -------------------- Helpers -------------------- */

/* Normalize attachments */
const normalizeAttachmentsFromDocumentReference = (resource) => {
  const result = [];
  if (!resource) return result;

  if (Array.isArray(resource.content)) {
    resource.content.forEach((c, idx) => {
      const att = c.attachment;
      if (!att) return;
      result.push({
        title: att.title || att.label || `content ${idx + 1}`,
        url: att.url || null,
        data: att.data || null,
        contentType: att.contentType || null,
        creation: att.creation || null,
        source: "content",
        raw: att,
      });
    });
  }

  if (Array.isArray(resource.presentedForm)) {
    resource.presentedForm.forEach((att, idx) => {
      result.push({
        title: att.title || `presentedForm ${idx + 1}`,
        url: att.url || null,
        data: att.data || null,
        contentType: att.contentType || null,
        creation: att.creation || null,
        source: "presentedForm",
        raw: att,
      });
    });
  }

  return result;
};


/* -------------------- Main Component -------------------- */
const DocumentReferenceCardExtended = ({ resource, handleShowNested, fhirClient }) => {
  const [showModal, setShowModal] = useState(false);
  const [modalBlobUrl, setModalBlobUrl] = useState("");
  const [modalTitle, setModalTitle] = useState("");
  const [modalType, setModalType] = useState("generic");
  const attachments = normalizeAttachmentsFromDocumentReference(resource);
  const [patientData, setPatientData] = useState(null);
  const [provider, setProvider] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [encounter, setEncounter] = useState(null);
  const [loading, setLoading] = useState({ patient: false, provider: false, org: false, encounter: false });
  const store = useStore();


  console.log(resource);

  useEffect(() => {
    // Reset when resource changes
    setPatientData(null);
    setProvider(null);
    setOrganization(null);
    setEncounter(null);

    if (!resource) return;

    // Fetch patient (subject)
    const doFetchPatient = async () => {
      if (!resource?.subject?.reference || !fhirClient) return;
      try {
        setLoading((l) => ({ ...l, patient: true }));
        const p = await fetchPatientByReference(resource.subject.reference, fhirClient, store);
        setPatientData(p);
      } catch (err) {
        console.error("ObservationCardExtended: failed to fetch patient", err);
        setPatientData(null);
      } finally {
        setLoading((l) => ({ ...l, patient: false }));
      }
    };

    // Parse performer array: we'll look for Practitioner and Organization refs
    const doFetchAuthors = async () => {
      if (!Array.isArray(resource.author) || !fhirClient) return;

      // performer entries can be Reference objects or full resources
      // We'll prefer Practitioner and Organization if present
      let practitionerRef = null;
      let orgRef = null;

      for (const p of resource.author) {
        const ref = p?.reference || (p?.identifier ? null : null);
        if (!ref) {
          // if a full resource object is passed inline
          if (p?.resourceType === "Practitioner") {
            setProvider(p);
            continue;
          }
          if (p?.resourceType === "Organization") {
            setOrganization(p);
            continue;
          }
          continue;
        }

        if (ref.startsWith("Practitioner/") && !practitionerRef) practitionerRef = ref;
        if (ref.startsWith("Organization/") && !orgRef) orgRef = ref;
      }

      // Fetch Practitioner via providerService if we have a ref
      if (practitionerRef) {
        try {
          setLoading((l) => ({ ...l, provider: true }));
          const prov = await fetchProviderByReference(practitionerRef, fhirClient, store);
          if (prov) setProvider(prov);
        } catch (err) {
          console.error("ObservationCardExtended: failed to fetch provider", err);
        } finally {
          setLoading((l) => ({ ...l, provider: false }));
        }
      }

      // Fetch Organization directly
      if (orgRef) {
        try {
          setLoading((l) => ({ ...l, org: true }));
          const org = await fetchOrganizationByReference(orgRef, fhirClient, store);
          setOrganization(org);
        } catch (err) {
          console.error("ObservationCardExtended: failed to fetch organization", err);
          setOrganization(null);
        } finally {
          setLoading((l) => ({ ...l, org: false }));
        }
      }
    };

    // Fetch encounter
    const doFetchEncounter = async () => {
      console.log("Checking for encounter reference:", resource?.context?.encounter?.[0]?.reference);
      if (!resource?.context?.encounter?.[0]?.reference || !fhirClient) return;
      try {
        setLoading((l) => ({ ...l, encounter: true }));
        const enc = await fetchEncounterByReference(resource.context.encounter[0].reference, fhirClient, store);
        console.log("Fetched encounter:", enc);
        setEncounter(enc);
      } catch (err) {
        console.error("DocumentReferenceCardExtended: failed to fetch encounter", err);
        setEncounter(null);
      } finally {
        setLoading((l) => ({ ...l, encounter: false }));
      }
    };

    doFetchPatient();
    doFetchAuthors();
    doFetchEncounter();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource, fhirClient]);



  return (
    <div className="p-4 border rounded shadow-sm bg-white">

      {/* ===================== SUBJECT + PERFORMER (Patient, Provider, Org) ===================== */}
       <CardHeader resource={resource} handleShowNested={handleShowNested} fhirClient={fhirClient} />

      {/* Identification */}
      <SectionHeader>Identification</SectionHeader>
     <div className={styles.indented}>
        <div className={styles.sectionContent}>
        <Field label="Document ID">{resource.id}</Field>
        <Field label="Status">{resource.status}</Field>
        <Field label="Doc Status">{resource.docStatus}</Field>
        <Field label="Type">{renderCodeable(resource.type)}</Field>
        {Array.isArray(resource.identifier) && resource.identifier.length > 0 && (
          <Field label="Identifiers">
            {resource.identifier.map((id, idx) => (
              <div key={idx} className="ps-3">{id.system} — {id.value}</div>
            ))}
          </Field>
        )}
      </div></div>

      {/* Context */}
      <SectionHeader>Context</SectionHeader>
      <div className={styles.indented}>
         <div className={styles.sectionContent}>
        <Field label="Encounter">
          {encounter ? (
            <button
              className="btn btn-link p-0"
              onClick={() => handleShowNested?.(encounter, resolveName(encounter, store))}
            >
              {resolveName(encounter, store)}
            </button>
          ) : resource.context?.encounter?.[0]?.reference ? (
            <span className="text-muted">{resource.context.encounter[0].reference}</span>
          ) : (
            <span className="text-muted">N/A</span>
          )}
        </Field>
        <Field label="Event">{resource.context?.event?.map((e, i) => <div key={i}>{renderCodeable(e)}</div>)}</Field>
        <Field label="Period">{resource.context?.period ? `${resource.context.period.start || "?"} → ${resource.context.period.end || "?"}` : "N/A"}</Field>
        <Field label="Facility Type">{renderCodeable(resource.context?.facilityType)}</Field>
      </div></div>

      {/* Content (attachments) */}
      <SectionHeader>Content</SectionHeader>
      <AttachmentHandler attachments={attachments} fhirClient={fhirClient} />

      {/* Relations */}
      {Array.isArray(resource.relatesTo) && resource.relatesTo.length > 0 && (
        <>
          <SectionHeader>Relations</SectionHeader>
          <div className={styles.indented}>
            {resource.relatesTo.map((r, i) => (
              <div key={i} className="mb-2">
                <strong>{r.code}:</strong> {renderReference(r.target?.reference, r.target?.display || r.target?.reference, handleShowNested)}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Notes */}
      {Array.isArray(resource.note) && resource.note.length > 0 && (
        <>
          <SectionHeader>Notes</SectionHeader>
          <div className={styles.indented}>
            {resource.note.map((n, i) => (
              <div key={i} className="mb-2">
                {n.text} <span className="text-muted small">({n.authorString || n.authorReference?.reference})</span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Metadata */}
      <SectionHeader>Metadata</SectionHeader>
    <div className={styles.indented}>
        <Field label="Language">{resource.language}</Field>
        <Field label="Created">{resource.created}</Field>
        <Field label="Indexed">{resource.indexed}</Field>
      </div>

      {/* Attachment modal */}
      <Modal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          if (modalBlobUrl) {
            URL.revokeObjectURL(modalBlobUrl);
            setModalBlobUrl("");
          }
        }}
        size="xl"
        fullscreen="md-down"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: "80vh" }}>
          {modalType === "pdf" && <PDFViewer blobUrl={modalBlobUrl} />}
          {modalType === "image" && <img src={modalBlobUrl} alt={modalTitle} style={{ maxWidth: "100%", maxHeight: "100%" }} />}
          {(modalType === "text" || modalType === "json" || modalType === "html") && (
            <iframe src={modalBlobUrl} title={modalTitle} style={{ width: "100%", height: "100%", border: "none" }} />
          )}
          {modalType === "generic" && <p className="text-muted text-center">Preview not supported for this file type.</p>}
        </Modal.Body>
      </Modal>
    </div>
  );


};

export default DocumentReferenceCardExtended;
